import React from "react";

/** The Sentinel Crimson cinematic stage — the layered VFX backdrop used by the
 * auth surfaces (login / signup / forgot-password).
 *
 * Layer order (see index.css for the animations):
 *   z0  client reference artwork (landscape on desktop, portrait on mobile)
 *   z1  vignette + floor bounce so text stays legible over the art
 *   z2  concentric machined rings orbiting the shield
 *   z3  vertical laser descending through the padlock
 *   z4  circuit-trace nodes + traces pulsing along the board
 *   z5  volumetric smoke drift, horizontal scan bar, film grain
 * Everything is `pointer-events-none` and collapses under
 * `prefers-reduced-motion`. */
const NODES = [
    { top: "12%", left: "34%", amber: false, delay: "0s" },
    { top: "26%", left: "22%", amber: true, delay: "0.6s" },
    { top: "41%", left: "38%", amber: false, delay: "1.1s" },
    { top: "58%", left: "19%", amber: true, delay: "0.3s" },
    { top: "70%", left: "35%", amber: false, delay: "1.6s" },
    { top: "18%", left: "62%", amber: true, delay: "2.1s" },
    { top: "48%", left: "72%", amber: false, delay: "0.9s" },
    { top: "78%", left: "58%", amber: true, delay: "1.4s" },
];

const TRACES = [
    { top: "20%", left: "24%", width: "16%", delay: "0s" },
    { top: "44%", left: "30%", width: "22%", delay: "0.8s" },
    { top: "63%", left: "20%", width: "28%", delay: "1.5s" },
    { top: "33%", left: "58%", width: "20%", delay: "2.2s" },
];

const RINGS = [
    { size: 420, cls: "" },
    { size: 620, cls: "sentinel-ring--2" },
    { size: 860, cls: "sentinel-ring--3" },
];

export function SentinelStage({ testId = "sentinel-stage" }) {
    return (
        <div className="sentinel-stage sentinel-grain" data-testid={testId} aria-hidden="true">
            {/* z0 — reference artwork */}
            <picture>
                <source media="(max-width: 1023px)" srcSet="/assets/sentinel/hud-portrait.webp" />
                <source media="(min-width: 1024px)" srcSet="/assets/sentinel/hud-amber.webp" />
                <img
                    src="/assets/sentinel/hud-landscape.webp"
                    alt=""
                    className="sentinel-backdrop theme-photo"
                    data-testid="sentinel-backdrop"
                />
            </picture>

            {/* z1 */}
            <div className="sentinel-vignette" style={{ zIndex: 1 }} />
            <div className="sentinel-floor" style={{ zIndex: 1 }} />

            {/* z2 — machined rings over the shield */}
            <div className="sentinel-rings" style={{ zIndex: 2 }}>
                {RINGS.map((r) => (
                    <span
                        key={r.size}
                        className={`sentinel-ring ${r.cls}`}
                        style={{ width: r.size, height: r.size, marginLeft: -r.size / 2, marginTop: -r.size / 2 }}
                    />
                ))}
            </div>

            {/* z3 — laser through the padlock */}
            <div className="sentinel-laser" style={{ zIndex: 3 }} />

            {/* z4 — circuit traces + nodes */}
            <div className="absolute inset-0" style={{ zIndex: 4 }}>
                {TRACES.map((t, i) => (
                    <span key={`t-${i}`} className="sentinel-trace" style={{ top: t.top, left: t.left, width: t.width, animationDelay: t.delay }} />
                ))}
                {NODES.map((n, i) => (
                    <span
                        key={`n-${i}`}
                        className={`sentinel-node ${n.amber ? "sentinel-node--amber" : ""}`}
                        style={{ top: n.top, left: n.left, animationDelay: n.delay }}
                    />
                ))}
            </div>

            {/* z5 — smoke + scan bar */}
            <div className="sentinel-smoke" style={{ zIndex: 5 }} />
            <div className="sentinel-scan" style={{ zIndex: 5 }} />
        </div>
    );
}
